<template>
  <div>
    <!-- Navigation Bar -->
    <nav class="navbar navbar-expand-lg navbar-dark bg-primary">
      <div class="container">
        <router-link class="navbar-brand fw-bold" to="/gallery">Design.AppVido</router-link>
        
        <div class="navbar-nav ms-auto d-flex align-items-center">
          <div class="nav-item dropdown">
            <a class="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false" ref="dropdownToggle">
              {{ user.full_name }}
            </a>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><router-link class="dropdown-item" to="/gallery">Gallery</router-link></li>
              <li><router-link class="dropdown-item" to="/settings">Settings</router-link></li>
              <li v-if="user.is_admin"><router-link class="dropdown-item" to="/admin">Admin Panel</router-link></li>
              <li><hr class="dropdown-divider"></li>
              <li><a class="dropdown-item" href="#" @click="logout">Logout</a></li>
            </ul>
          </div>
        </div>
      </div>
    </nav>

    <div class="container mt-4">
      <div class="row justify-content-center">
        <div class="col-md-8">
          <h2 class="mb-4">Account Settings</h2>
          
          <!-- Profile Information -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Profile Information</h5>
            </div>
            <div class="card-body">
              <div v-if="profileMessage" :class="['alert', profileMessage.type === 'success' ? 'alert-success' : 'alert-danger']">
                {{ profileMessage.text }}
              </div>
              
              <form @submit.prevent="updateProfile">
                <div class="mb-3">
                  <label for="fullName" class="form-label">Full Name</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    id="fullName" 
                    v-model="profileForm.full_name"
                    required
                  >
                </div>
                
                <div class="mb-3">
                  <label for="email" class="form-label">Email Address</label>
                  <input 
                    type="email" 
                    class="form-control" 
                    id="email" 
                    :value="user.email"
                    disabled
                  >
                  <small class="form-text text-muted">Email cannot be changed</small>
                </div>
                
                <div class="mb-3">
                  <label for="phone" class="form-label">Phone Number</label>
                  <input 
                    type="tel" 
                    class="form-control" 
                    id="phone" 
                    v-model="profileForm.phone"
                  >
                </div>
                
                <button 
                  type="submit" 
                  class="btn btn-primary"
                  :disabled="profileLoading"
                >
                  <span v-if="profileLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ profileLoading ? 'Updating...' : 'Update Profile' }}
                </button>
              </form>
            </div>
          </div>
          
          <!-- Change Password -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">Change Password</h5>
            </div>
            <div class="card-body">
              <div v-if="passwordMessage" :class="['alert', passwordMessage.type === 'success' ? 'alert-success' : 'alert-danger']">
                {{ passwordMessage.text }}
              </div>
              
              <form @submit.prevent="changePassword">
                <div class="mb-3">
                  <label for="currentPassword" class="form-label">Current Password</label>
                  <input 
                    type="password" 
                    class="form-control" 
                    id="currentPassword" 
                    v-model="passwordForm.current_password"
                    required
                  >
                </div>
                
                <div class="mb-3">
                  <label for="newPassword" class="form-label">New Password</label>
                  <input 
                    type="password" 
                    class="form-control" 
                    id="newPassword" 
                    v-model="passwordForm.new_password"
                    required
                    minlength="6"
                  >
                  <small class="form-text text-muted">Password must be at least 6 characters</small>
                </div>
                
                <div class="mb-3">
                  <label for="confirmPassword" class="form-label">Confirm New Password</label>
                  <input 
                    type="password" 
                    class="form-control" 
                    id="confirmPassword" 
                    v-model="confirmPassword"
                    required
                    minlength="6"
                  >
                </div>
                
                <button 
                  type="submit" 
                  class="btn btn-primary"
                  :disabled="passwordLoading || passwordForm.new_password !== confirmPassword"
                >
                  <span v-if="passwordLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ passwordLoading ? 'Changing...' : 'Change Password' }}
                </button>
                
                <div v-if="passwordForm.new_password && confirmPassword && passwordForm.new_password !== confirmPassword" class="text-danger mt-2">
                  Passwords do not match
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Dropdown } from 'bootstrap'

export default {
  name: 'SettingsPage',
  data() {
    return {
      user: {},
      profileForm: {
        full_name: '',
        phone: ''
      },
      passwordForm: {
        current_password: '',
        new_password: ''
      },
      confirmPassword: '',
      profileLoading: false,
      passwordLoading: false,
      profileMessage: null,
      passwordMessage: null
    }
  },
  methods: {
    async loadUserData() {
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        const response = await axios.get('https://designappvido-backend.up.railway.app/api/me', config)
        this.user = response.data
        
        // Update form with current data
        this.profileForm.full_name = this.user.full_name
        this.profileForm.phone = this.user.phone || ''
        
      } catch (error) {
        console.error('Failed to load user data:', error)
        if (error.response?.status === 401) {
          this.logout()
        }
      }
    },
    
    async updateProfile() {
      this.profileLoading = true
      this.profileMessage = null
      
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.put('https://designappvido-backend.up.railway.app/api/me', this.profileForm, config)
        
        // Update local user data
        this.user.full_name = this.profileForm.full_name
        this.user.phone = this.profileForm.phone
        
        this.profileMessage = {
          type: 'success',
          text: 'Profile updated successfully!'
        }
        
      } catch (error) {
        this.profileMessage = {
          type: 'error',
          text: error.response?.data?.detail || 'Failed to update profile'
        }
      } finally {
        this.profileLoading = false
      }
    },
    
    async changePassword() {
      this.passwordLoading = true
      this.passwordMessage = null
      
      if (this.passwordForm.new_password !== this.confirmPassword) {
        this.passwordMessage = {
          type: 'error',
          text: 'Passwords do not match'
        }
        this.passwordLoading = false
        return
      }
      
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        await axios.post('https://designappvido-backend.up.railway.app/api/change-password', this.passwordForm, config)
        
        this.passwordMessage = {
          type: 'success',
          text: 'Password changed successfully!'
        }
        
        // Reset form
        this.passwordForm = {
          current_password: '',
          new_password: ''
        }
        this.confirmPassword = ''
        
      } catch (error) {
        this.passwordMessage = {
          type: 'error',
          text: error.response?.data?.detail || 'Failed to change password'
        }
      } finally {
        this.passwordLoading = false
      }
    },
    
    async logout() {
      try {
        const token = localStorage.getItem('token')
        if (token) {
          // Call backend logout endpoint
          await axios.post('https://designappvido-backend.up.railway.app/api/logout', {}, {
            headers: { Authorization: `Bearer ${token}` }
          })
        }
      } catch (error) {
        console.error('Logout error:', error)
        // Continue with local logout even if backend call fails
      } finally {
        // Always clear local storage and redirect
        localStorage.removeItem('token')
        this.$router.push('/')
      }
    }
  },
  
  async mounted() {
    // Token validation is now handled by router guard
    // Just load user data since we know the token is valid
    try {
      await this.loadUserData()
    } catch (error) {
      if (error.response?.status === 401) {
        // Fallback: if session becomes invalid after router guard check
        localStorage.removeItem('token')
        this.$router.push('/')
        return
      }
    }
    
    // Initialize Bootstrap dropdown manually
    this.$nextTick(() => {
      if (this.$refs.dropdownToggle) {
        new Dropdown(this.$refs.dropdownToggle)
      }
    })
  }
}
</script>

<style scoped>
.bg-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.card {
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 10px 10px 0 0 !important;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
}
</style>